import React, { useState } from 'react';
import { Check, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import BookingModal from './BookingModal';

const Pricing = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const handleBookNow = (packageId) => {
    setSelectedPackage(packageId);
    setIsBookingModalOpen(true);
  };
  const pricingTiers = [
    {
      package_id: 'mini',
      name: 'Mini Load',
      price: '$95',
      description: 'Quick pickup',
      image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&q=80',
      features: [
        '1 furniture item',
        '1-2 garbage bags',
        'Perfect for small items',
        'Same-day service available',
        '15% off if junk is outside',
        'Eco-friendly disposal'
      ]
    },
    {
      package_id: 'small',
      name: 'Small Load',
      price: '$175',
      description: 'Perfect for small cleanups',
      image: 'https://images.unsplash.com/photo-1729628371767-7a833756b8b5?w=800&q=80',
      features: [
        'Up to 1/4 truck load',
        'Single room cleanout',
        'Few furniture items',
        'Same-day service available',
        '15% off if junk is outside',
        'Eco-friendly disposal'
      ]
    },
    {
      package_id: 'medium',
      name: 'Medium Load',
      price: '$325',
      description: 'Most popular option',
      popular: true,
      image: 'https://images.unsplash.com/photo-1762805544550-f12a8ebceb2e?w=800&q=80',
      features: [
        'Up to 1/2 truck load',
        'Multiple rooms',
        'Furniture & appliances',
        'Same-day service available',
        '15% off if junk is outside',
        'Eco-friendly disposal',
        'Free estimate'
      ]
    },
    {
      package_id: 'large',
      name: 'Large Load',
      price: '$450',
      description: 'For bigger cleanouts',
      image: 'https://images.unsplash.com/photo-1762805543729-361d38e78a37?w=800&q=80',
      features: [
        'Up to 3/4 truck load',
        'Multiple rooms',
        'Large furniture items',
        'Construction materials',
        'Same-day service available',
        '15% off if junk is outside',
        'Eco-friendly disposal',
        'Free estimate'
      ]
    },
    {
      package_id: 'full',
      name: 'Full Load',
      price: '$600+',
      description: 'Maximum capacity',
      image: 'https://images.unsplash.com/photo-1762805543729-361d38e78a37?w=800&q=80',
      features: [
        'Full truck load or more',
        'Our truck holds up to 14 yards',
        'Whole house cleanout',
        'Construction debris',
        'Commercial projects',
        '15% off if junk is outside',
        'Eco-friendly disposal',
        'Free estimate'
      ]
    }
  ];

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Transparent Pricing
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Our pricing is based on volume, not time. You only pay for the space your junk takes up in our truck.
          </p>
        </div>

        {/* Truck Load Comparison Visual */}
        <div className="mb-16 bg-emerald-50 rounded-2xl p-8 relative overflow-hidden">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Truck Load Size Comparison</h3>
            <p className="text-gray-600">See exactly what each load size looks like in our truck</p>
          </div>
          <div className="relative max-w-4xl mx-auto">
            <img 
              src="https://customer-assets.emergentagent.com/job_haul-away-pro/artifacts/njr5pkg5_junk010.jpg" 
              alt="Truck load comparison"
              className="w-full h-auto rounded-lg shadow-xl"
            />
          </div>
          
          {/* 15% Discount Banner */}
          <div className="mt-8 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl p-6 text-center shadow-lg">
            <div className="flex items-center justify-center gap-3 mb-2">
              <span className="text-4xl font-bold">15% OFF</span>
            </div>
            <p className="text-lg font-semibold mb-1">Special Discount Available!</p>
            <p className="text-emerald-100">Save 15% when all junk is outside and ready for removal</p>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {pricingTiers.map((tier, index) => (
            <Card 
              key={index} 
              className={`relative overflow-hidden transition-all duration-300 ${
                tier.popular 
                  ? 'border-2 border-emerald-600 shadow-xl scale-105' 
                  : 'border-2 border-gray-200 hover:border-emerald-400 hover:shadow-lg'
              }`}
            >
              {tier.popular && (
                <div className="absolute top-0 right-0 bg-emerald-600 text-white px-4 py-1 text-sm font-semibold z-10">
                  Most Popular
                </div>
              )}
              <CardHeader className="text-center pb-4 pt-6">
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  {tier.name}
                </CardTitle>
                <div className="flex items-baseline justify-center gap-1 mb-2">
                  <span className="text-4xl font-bold text-emerald-600">{tier.price}</span>
                  <span className="text-sm text-gray-500">+ HST</span>
                </div>
                <p className="text-gray-600 text-sm">{tier.description}</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <Check className="w-5 h-5 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => handleBookNow(tier.package_id)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 font-semibold transition-colors duration-300"
                >
                  Book Now
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Info */}
        <div className="bg-emerald-50 p-8 rounded-xl text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Need a Custom Quote?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Every job is unique. Our trucks hold up to 14 yards. Call us for a free, no-obligation estimate based on your specific needs.
          </p>
          <a href="tel:3653363364">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center">
              <Phone className="w-5 h-5 mr-2" />
              Call Now: (365) 336-3364
            </Button>
          </a>
        </div>
      </div>
      
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        preselectedPackage={selectedPackage}
      />
    </section>
  );
};

export default Pricing;